// src/components/ui/Button.tsx

import { Slot } from "@radix-ui/react-slot"
import RiLoader2Fill from "~/icons/remix/generated/RiLoader2Fill"
import RiArrowRightLine from "~/icons/remix/generated/RiArrowRightLine"
import { type ComponentPropsWithoutRef, forwardRef } from "react"
import { type VariantProps, tv } from "tailwind-variants"
import { cn, focusRing } from "~/lib/utils/design"

const buttonVariants = tv({
  base: [
    "relative inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200",
    "shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900",
    "disabled:pointer-events-none disabled:opacity-50",
    focusRing
  ],
  variants: {
    variant: {
      primary: [
        "bg-gradient-to-r from-indigo-600 to-blue-600 text-white",
        "hover:from-indigo-500 hover:to-blue-500",
        "focus:ring-indigo-500",
        "disabled:from-indigo-400 disabled:to-blue-400"
      ],
      secondary: [
        "bg-white text-gray-900 ring-1 ring-gray-900/10",
        "hover:bg-gray-50",
        "dark:bg-gray-900 dark:text-white dark:ring-white/10",
        "dark:hover:bg-gray-800",
        "focus:ring-gray-500"
      ],
      outline: [
        "bg-white text-gray-700 ring-1 ring-gray-900/10",
        "hover:bg-gray-50 hover:text-gray-900",
        "dark:bg-gray-900 dark:text-gray-400 dark:ring-white/10",
        "dark:hover:bg-gray-800 dark:hover:text-white",
        "focus:ring-gray-500"
      ],
      destructive: [
        "bg-red-600 text-white",
        "hover:bg-red-700",
        "dark:bg-red-700 dark:hover:bg-red-600",
        "focus:ring-red-500",
        "disabled:bg-red-400"
      ],
      ghost: [
        "bg-transparent text-gray-900 hover:bg-gray-100",
        "dark:text-gray-100 dark:hover:bg-gray-800",
        "focus:ring-gray-500"
      ]
    },
    size: {
      sm: "px-3 py-2 text-sm gap-2",
      md: "px-4 py-2 text-base gap-2",
      lg: "px-8 py-4 text-lg gap-3"
    },
    fullWidth: {
      true: "w-full"
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "md"
  }
})

interface ButtonProps
  extends ComponentPropsWithoutRef<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  loadingText?: string
  showArrow?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild,
      isLoading = false,
      loadingText,
      showArrow = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      variant,
      size,
      fullWidth,
      children,
      ...props
    }: ButtonProps,
    forwardedRef
  ) => {
    const Component = asChild ? Slot : "button"

    const iconSize = {
      sm: "h-4 w-4",
      md: "h-5 w-5", 
      lg: "h-6 w-6"
    }[size ?? "md"]

    return (
      <Component
        ref={forwardedRef}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled ?? isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="pointer-events-none flex items-center justify-center gap-2">
            <RiLoader2Fill className={cn(iconSize, "animate-spin")} aria-hidden="true" />
            <span>{loadingText ?? children}</span>
          </span>
        ) : (
          <>
            {leftIcon && <span className={iconSize}>{leftIcon}</span>}
            {children}
            {rightIcon && <span className={iconSize}>{rightIcon}</span>}
            {showArrow && <RiArrowRightLine className={iconSize} />}
          </>
        )}
      </Component>
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants, type ButtonProps }